import { getReplenishmentCalendar, pinReplenishmentInstrument, pinReplenishmentRequirement, refreshReplenishmentRun, type InstrumentRow, type RequirementRow } from '@api/replenishment-engine';
import useToaster from '@hooks/toaster/useToaster';
import { Badge, Button, Group, Stack, Text } from '@mantine/core';
import { formatCount } from '@utils/currency';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { addDaysKey, localDateKey } from './dates';
import classes from './engine.module.css';

const n = (v: number, maxDecimals = 0): string => formatCount(v, { maxDecimals });

/** The server's message, whatever shape the API client rejected with. */
const errorText = (e: unknown): string => {
    const err = e as { error?: { message?: string | string[] }; message?: string | string[] };
    const m = err?.error?.message ?? err?.message;
    return Array.isArray(m) ? m.join(', ') : (m ?? 'unknown error');
};
const todayKey = (): string => localDateKey();

export type PinEdit = { qty: number } | { clear: true };
/** A line's number before and after the last Re-plan, when it moved. */
export type QtyChange = { from: number; to: number };
export interface PinEdits {
    byId: Map<string, PinEdit>;
    onEdit: (instrumentId: string, edit: PinEdit | null) => void;
    /** Lines whose number the last Re-plan moved, by identity key — the violet flash. */
    changed?: Map<string, QtyChange>;
}

/** The same line across runs: proposals get new ids every run, so edits and diffs key on what the line is. */
export const lineKey = (r: Pick<InstrumentRow, 'type' | 'productId' | 'requirementSeq' | 'tranche' | 'itemId' | 'fromFacilityId' | 'toFacilityId'>): string =>
    `${r.type}|${r.productId}|${r.requirementSeq}|${r.tranche}|${r.itemId}|${r.fromFacilityId ?? ''}|${r.toFacilityId ?? ''}`;

/**
 * Edit → pin → Re-plan, shared by every table that takes a typed number: the edits gathered
 * on the page (by instrument id; `blocked:<requirement id>` for a dip no run could start),
 * pinned together on Re-plan, then one engine run that plans around all of them. After the
 * run, the lines whose number moved flash and a toast lists them. `bar` is the strip that
 * says how many edits wait and holds Discard / Re-plan.
 */
export const useReplan = ({ rows, blocked, windowStart, windowEnd, onChanged }: { rows: InstrumentRow[]; blocked: RequirementRow[]; windowStart: string | null; windowEnd: string | null; onChanged?: () => void | Promise<void> }): { pinEdits: PinEdits; planning: boolean; editCount: number; bar: ReactNode } => {
    // Edits are gathered here and sent together on Re-plan: each is pinned on its line, then
    // the engine runs once and plans around all of them.
    const [edits, setEdits] = useState<Map<string, PinEdit>>(new Map());
    const [planning, setPlanning] = useState(false);
    const [planError, setPlanError] = useState<string | null>(null);
    // What every line said before Re-plan, so the new plan can be read against it: the
    // boxes that moved flash violet and the toast lists them by SKU.
    const beforeReplan = useRef<Map<string, { sku: string; itemName: string | null; qty: number }> | null>(null);
    const [changed, setChanged] = useState<Map<string, QtyChange>>(new Map());
    const { showToastSuccess } = useToaster();
    useEffect(() => {
        const before = beforeReplan.current;
        if (!before) return;
        beforeReplan.current = null;
        const moved = new Map<string, QtyChange>();
        const lines: string[] = [];
        for (const r of rows) {
            if (r.status !== 'PROPOSED') continue;
            const was = before.get(lineKey(r));
            if (!was || was.qty === r.qty) continue;
            moved.set(lineKey(r), { from: was.qty, to: r.qty });
            lines.push(`${r.sku}${r.itemKind === 'MATERIAL' ? ` · ${r.itemName ?? r.itemId}` : ''}: ${n(was.qty, r.unit ? 1 : 0)} → ${n(r.qty, r.unit ? 1 : 0)}`);
        }
        const gone = before.size - rows.filter((r) => r.status === 'PROPOSED' && before.has(lineKey(r))).length;
        const added = rows.filter((r) => r.status === 'PROPOSED' && !before.has(lineKey(r))).length;
        setChanged(moved);
        const shown = lines.slice(0, 6);
        const more = lines.length - shown.length;
        const extras = [gone > 0 ? `${gone} line${gone === 1 ? '' : 's'} dropped` : '', added > 0 ? `${added} new line${added === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · ');
        showToastSuccess({
            title: moved.size === 0 && gone === 0 && added === 0 ? 'Re-planned — nothing moved' : `Re-planned — ${moved.size} number${moved.size === 1 ? '' : 's'} changed`,
            autoClose: 8000,
            subtitle: (
                <Stack gap={2}>
                    {shown.map((line) => (
                        <Text key={line} size="xs">
                            {line}
                        </Text>
                    ))}
                    {more > 0 && (
                        <Text size="xs" c="dimmed">
                            +{more} more
                        </Text>
                    )}
                    {extras && (
                        <Text size="xs" c="dimmed">
                            {extras}
                        </Text>
                    )}
                </Stack>
            ),
        });
    }, [rows, showToastSuccess]);
    const pinEdits = useMemo<PinEdits>(
        () => ({
            byId: edits,
            changed,
            onEdit: (id, edit) =>
                setEdits((e) => {
                    const next = new Map(e);
                    if (edit) next.set(id, edit);
                    else next.delete(id);
                    return next;
                }),
        }),
        [edits, changed],
    );
    const editCount = edits.size;
    const pinnedCount = rows.filter((r) => r.status === 'PROPOSED' && r.pinned?.qty != null).length;
    /**
     * Pins one edited line. Ids live only as long as a run: every run (a sync, a refresh, the
     * worker) rebuilds the proposed lines with new ids, so a page opened before it holds ids
     * that are gone. When the id is gone, the line is found again by what it is (type, SKU,
     * dip, part, item, route) in the current plan and pinned there; a production or FG order
     * that no longer has a matching line is pinned as the SKU's run on its own start date.
     */
    const pinLine = useCallback(
        async (id: string, edit: PinEdit): Promise<void> => {
            const body = 'qty' in edit ? { ...edit, until: windowEnd ?? undefined } : edit;
            const today = todayKey();
            // An objective with its own L / R is a preview: the engine ran in memory and its lines
            // were never written, so their ids (`preview:…`) are in no table. The preview reads
            // its pins from the stored plan, so the number is pinned there, as the SKU's run (or
            // FG order) on the preview line's own start date, or on the stored order of the same
            // material and site.
            if (id.startsWith('preview:')) {
                const line = rows.find((r) => r.id === id);
                if (!line) throw new Error('this line is no longer on the page. Reload and type it again.');
                if (!('qty' in edit)) return;
                if (line.type === 'WORK_ORDER' || line.type === 'FG_PO') {
                    await pinReplenishmentRequirement({ productId: line.productId, qty: edit.qty, startDate: line.placeBy < today ? today : line.placeBy, until: windowEnd ?? undefined });
                    return;
                }
                const stored = await getReplenishmentCalendar(today, addDaysKey(line.placeBy > today ? line.placeBy : today, 60), 'PROPOSED');
                const same = stored.rows.find((r) => r.type === line.type && r.itemId === line.itemId && (r.toFacilityId ?? '') === (line.toFacilityId ?? '') && (r.fromFacilityId ?? '') === (line.fromFacilityId ?? '') && r.placeBy === line.placeBy) ?? stored.rows.find((r) => r.type === line.type && r.itemId === line.itemId && (r.toFacilityId ?? '') === (line.toFacilityId ?? ''));
                if (!same) throw new Error(`${line.itemName ?? line.itemId}: the saved plan has no order for it to fix. Run the plan with these levels first, or fix the number on the production line it serves.`);
                await pinReplenishmentInstrument(same.id, body);
                return;
            }
            try {
                await pinReplenishmentInstrument(id, body);
                return;
            } catch (e) {
                if (!/not found/i.test(errorText(e))) throw e;
            }
            const line = rows.find((r) => r.id === id);
            if (!line) throw new Error('the plan was re-run since this page loaded and the line is no longer in it. Reload the page and type the number again.');
            const current = await getReplenishmentCalendar(today, addDaysKey(line.placeBy > today ? line.placeBy : today, 60), 'PROPOSED');
            const match = current.rows.find((r) => lineKey(r) === lineKey(line));
            if (match) {
                await pinReplenishmentInstrument(match.id, body);
                return;
            }
            if ('qty' in edit && (line.type === 'WORK_ORDER' || line.type === 'FG_PO')) {
                await pinReplenishmentRequirement({ productId: line.productId, qty: edit.qty, startDate: line.placeBy < today ? today : line.placeBy, until: windowEnd ?? undefined });
                return;
            }
            throw new Error(`${line.itemName ?? line.itemId} is no longer in the plan (it was re-run since this page loaded). Reload the page and type it again.`);
        },
        [rows, windowEnd],
    );
    const replan = useCallback(async () => {
        setPlanning(true);
        setPlanError(null);
        setChanged(new Map());
        try {
            for (const [id, edit] of edits) {
                // A dip no run could start has no line yet: its number becomes a pinned run.
                const dip = id.startsWith('blocked:') ? blocked.find((b) => `blocked:${b.id}` === id) : null;
                if (dip) {
                    if ('qty' in edit && edit.qty > 0) await pinReplenishmentRequirement({ productId: dip.productId, qty: edit.qty, startDate: windowStart ?? undefined, until: windowEnd ?? undefined });
                } else await pinLine(id, edit);
            }
            await refreshReplenishmentRun();
            setEdits(new Map());
            beforeReplan.current = new Map(rows.filter((r) => r.status === 'PROPOSED').map((r) => [lineKey(r), { sku: r.sku, itemName: r.itemName, qty: r.qty }]));
            await onChanged?.();
        } catch (e) {
            beforeReplan.current = null;
            setPlanError(`Could not re-plan: ${errorText(e)}`);
        } finally {
            setPlanning(false);
        }
    }, [edits, rows, onChanged, blocked, windowStart, windowEnd, pinLine]);
    const bar: ReactNode =
        (editCount > 0 || pinnedCount > 0 || planError) ? (
            <Group className={classes.bar} justify="space-between" align="center">
                <Group gap="xs" align="center" wrap="nowrap">
                    {editCount > 0 ? (
                        <>
                            <Badge size="sm" color="orange" variant="light" tt="none">
                                {editCount} edit{editCount === 1 ? '' : 's'} not planned yet
                            </Badge>
                            <Text size="xs" c="dimmed">
                                Re-plan reads the plan again around every number you fixed; the rest follows.
                            </Text>
                        </>
                    ) : (
                        <Text size="xs" c="dimmed">
                            {pinnedCount} line{pinnedCount === 1 ? '' : 's'} fixed by hand · the engine plans around them every run; unpin a line to return it to the engine's number.
                        </Text>
                    )}
                    {planError && (
                        <Text size="xs" c="red">
                            {planError}
                        </Text>
                    )}
                </Group>
                <Group gap="xs" align="center" wrap="nowrap">
                    {changed.size > 0 && editCount === 0 && (
                        <Badge size="sm" color="violet" variant="light" tt="none">
                            {changed.size} number{changed.size === 1 ? '' : 's'} moved on the last re-plan
                        </Badge>
                    )}
                    {editCount > 0 && (
                        <>
                            <Button size="xs" variant="subtle" onClick={() => setEdits(new Map())} disabled={planning}>
                                Discard
                            </Button>
                            <Button size="xs" onClick={() => void replan()} loading={planning}>
                                Re-plan with {editCount} edit{editCount === 1 ? '' : 's'}
                            </Button>
                        </>
                    )}
                </Group>
            </Group>
        ) : null;
    return { pinEdits, planning, editCount, bar };
};
